import type { DriverState, RaceRecording, RaceSnapshot } from './race';
import { buildSnapshot } from './replay';

/** One cursor cache: advance deltas once, rebuild only for a new recording or a backwards seek. */
export function createSnapshotProjector() {
  let recording: RaceRecording | null = null;
  let snapshot: RaceSnapshot | null = null;
  let cursor = 0, dataRevision = 0;
  let drivers = new Map<number, DriverState>();
  const messages = new Map<string, RaceSnapshot['messages'][number]>();
  const pits = new Map<string, RaceSnapshot['pitStops'][number]>();

  return (source: RaceRecording, elapsedMs: number): { snapshot: RaceSnapshot; dataRevision: number } => {
    const atMs = Math.max(0, Math.min(source.durationMs, Number.isFinite(elapsedMs) ? elapsedMs : 0));
    const reset = recording !== source || snapshot === null || atMs < snapshot.atMs;
    if (reset) {
      recording = source; cursor = 0;
      snapshot = buildSnapshot({ ...source, events: [] }, atMs);
      drivers = new Map(snapshot.drivers.map(driver => [driver.number, driver]));
      messages.clear(); pits.clear();
    }
    const previous = snapshot!;
    let changed = reset, driversChanged = false, messagesChanged = false, pitsChanged = false;
    let { session, weather, trackStatus, qualifyingPhase } = previous;
    while (cursor < source.events.length && source.events[cursor].atMs <= atMs) {
      const event = source.events[cursor++]; changed = true;
      if (event.type === 'timing') {
        const driver = drivers.get(event.driverNumber);
        if (driver) {
          const sectors = event.patch.sectors
            ? driver.sectors.map((value, index) => event.patch.sectors?.[index] ?? value) as DriverState['sectors']
            : undefined;
          drivers.set(event.driverNumber, { ...driver, ...event.patch,
            ...(sectors ? { sectors } : {}),
            ...(event.patch.stints ? { stints: event.patch.stints.map(stint => ({ ...stint })) } : {}) });
          driversChanged = true;
        }
      } else if (event.type === 'telemetry') {
        const old = drivers.get(event.driverNumber);
        if (!old) continue;
        const driver = { ...old, telemetry: { ...old.telemetry, ...event.patch } };
        if (event.patch.speed !== undefined || event.patch.rpm !== undefined || event.patch.throttle !== undefined || event.patch.brake !== undefined || event.patch.gear !== undefined || event.patch.drs !== undefined) driver.telemetryAtMs = event.atMs;
        if (event.patch.x !== undefined || event.patch.y !== undefined) driver.locationAtMs = event.atMs;
        if (event.atMs >= atMs - 30_000 && typeof event.patch.speed === 'number') driver.speedHistory = [...old.speedHistory, { atMs: event.atMs, speed: event.patch.speed }];
        drivers.set(event.driverNumber, driver); driversChanged = true;
      } else if (event.type === 'weather') weather = { ...event.weather };
      else if (event.type === 'status') trackStatus = event.status;
      else if (event.type === 'phase') qualifyingPhase = event.phase;
      else if (event.type === 'session') session = { ...session, ...event.patch };
      else if (event.type === 'message') { messages.set(event.message.id, { ...event.message }); messagesChanged = true; }
      else if (event.type === 'pit') { pits.set(event.stop.id, { ...event.stop }); pitsChanged = true; }
    }
    // The graph's rolling 30-second window can expire even when no new event arrives.
    for (const [number, driver] of drivers) {
      if (driver.speedHistory.length && driver.speedHistory[0].atMs < atMs - 30_000) {
        drivers.set(number, { ...driver, speedHistory: driver.speedHistory.filter(sample => sample.atMs >= atMs - 30_000) });
        changed = true; driversChanged = true;
      }
    }
    if (changed) {
      const sorted = driversChanged ? [...drivers.values()].sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity) || a.number - b.number) : previous.drivers;
      snapshot = { ...previous, atMs, session, drivers: sorted, weather, trackStatus, qualifyingPhase,
        messages: messagesChanged ? [...messages.values()].sort((a, b) => b.atMs - a.atMs) : previous.messages,
        pitStops: pitsChanged ? [...pits.values()].sort((a, b) => b.atMs - a.atMs) : previous.pitStops,
        lap: driversChanged ? Math.max(0, ...sorted.map(driver => driver.lap)) : previous.lap };
      dataRevision++;
    } else if (atMs !== previous.atMs) snapshot = { ...previous, atMs };
    return { snapshot: snapshot!, dataRevision };
  };
}
